// <nms-quote> — embeddable instant-quote calculator for National Media Services.
//
//   <script src="nms-quote.js" defer></script>
//   <nms-quote categories="garments,printing,cd-dvd"></nms-quote>
//
// Attributes
//   categories   comma list of category ids to show (default: all)
//   mode         "internal" enables the staff tools (custom costs, waivers)
//   config-src   URL of a JSON file that replaces bundled price config,
//                shaped { "categories": [ ...category configs ] }
//   submit-url   if set, the quote request is POSTed here as JSON
//   heading      title text (default "Instant Quote")
//
// Events (bubble out of the shadow root)
//   nms-quote:change  detail = { category, quote }
//   nms-quote:submit  detail = { category, order, quote, contact, createdAt }
//                     call preventDefault() to suppress the built-in thank-you screen.

import { styles } from "./styles.js";
import { esc, usd } from "./html.js";
import { quote as priceQuote } from "../engine/index.js";
import { validateArtwork, browserHasTransparentPixels } from "../engine/artwork.js";
import { parseRosterCsv, rosterTemplateCsv, resolveSize } from "../engine/roster.js";
import { garmentForm, garmentTicket, garmentOrder, newGarmentState, newGarmentLine, newDecal } from "./views-garment.js";
import { tieredForm, tieredTicket, newTieredState, normalizeTiered, suggestUp } from "./views-tiered.js";
import { printGarmentQuote, printWaiver } from "./print.js";

import garments from "../../config/garments.json" with { type: "json" };
import printing from "../../config/printing.json" with { type: "json" };
import cdDvd from "../../config/cd-dvd.json" with { type: "json" };
import businessCards from "../../config/business-cards.json" with { type: "json" };

const BUNDLED = [garments, printing, businessCards, cdDvd];
let uidCounter = 0;

function setPath(root, path, value) {
  const keys = path.split(".");
  let o = root;
  for (const k of keys.slice(0, -1)) o = o[k];
  o[keys[keys.length - 1]] = value;
}

/** Stable selector for an element so focus survives a re-render. */
function focusKey(el) {
  if (!el || !el.dataset) return null;
  const d = el.dataset;
  if (d.path) return el.type === "radio" ? `[data-path="${d.path}"][value="${CSS.escape(el.value)}"]` : `[data-path="${d.path}"]`;
  if (d.addon) return `[data-addon="${d.addon}"]`;
  if (d.action) return `[data-action="${d.action}"]` + ["line", "decal", "row", "kind", "qty"].filter((k) => d[k] != null).map((k) => `[data-${k}="${d[k]}"]`).join("");
  if (el.id) return `#${CSS.escape(el.id)}`;
  return null;
}

class NmsQuote extends HTMLElement {
  static observedAttributes = ["categories", "mode", "config-src", "heading"];

  constructor() {
    super();
    this.uid = `nmsq${++uidCounter}`;
    this.root = this.attachShadow({ mode: "open" });
    this.allCategories = BUNDLED;
    this.state = null;
    this.lastTotal = null;
  }

  connectedCallback() {
    if (this.state) return;
    this.root.addEventListener("input", (e) => this.onInput(e));
    this.root.addEventListener("change", (e) => this.onChange(e));
    this.root.addEventListener("click", (e) => this.onClick(e));
    this.root.addEventListener("dragover", (e) => this.onDrag(e, true));
    this.root.addEventListener("dragleave", (e) => this.onDrag(e, false));
    this.root.addEventListener("drop", (e) => this.onDrop(e));
    this.load();
  }

  attributeChangedCallback(name, oldV, newV) {
    if (!this.state || oldV === newV) return;
    if (name === "config-src") this.load();
    else { this.initState(); this.render(); }
  }

  get mode() { return this.getAttribute("mode") === "internal" ? "internal" : "public"; }

  get categories() {
    const want = (this.getAttribute("categories") ?? "").split(",").map((s) => s.trim()).filter(Boolean);
    return want.length ? want.map((id) => this.allCategories.find((c) => c.id === id)).filter(Boolean) : this.allCategories;
  }

  get category() { return this.categories.find((c) => c.id === this.state.active) ?? this.categories[0]; }

  async load() {
    const src = this.getAttribute("config-src");
    if (src) {
      try {
        const res = await fetch(src, { credentials: "omit" });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const json = await res.json();
        const list = Array.isArray(json) ? json : json.categories;
        this.allCategories = BUNDLED.map((b) => list.find((c) => c.id === b.id) ?? b).concat(list.filter((c) => !BUNDLED.some((b) => b.id === c.id)));
      } catch (err) {
        console.warn(`[nms-quote] Could not load ${src}; using bundled prices.`, err);
        this.allCategories = BUNDLED;
      }
    }
    this.initState();
    this.render();
  }

  initState() {
    const cats = this.categories;
    const t = {};
    for (const c of cats) if (c.type === "tiered-table") t[c.id] = newTieredState(c);
    const g = cats.find((c) => c.type === "garment-formula");
    this.state = {
      active: cats[0]?.id,
      g: g ? newGarmentState(g) : null,
      t,
      contact: { name: "", email: "", phone: "", notes: "" },
      sent: null,
    };
  }

  /** Current order + quote for the active category. */
  compute() {
    const cat = this.category;
    if (!cat) return null;
    const order = cat.type === "garment-formula" ? garmentOrder(this.state.g, this.mode, cat) : this.state.t[cat.id];
    return { cat, order, quote: priceQuote(cat, order) };
  }

  /** Public API: the current quote object. */
  getQuote() { return this.compute()?.quote ?? null; }

  // ---------- rendering ----------

  render() {
    const active = this.root.activeElement;
    const key = focusKey(active);
    const caret = active && "selectionStart" in active ? (() => { try { return active.selectionStart; } catch { return null; } })() : null;

    const cats = this.categories;
    const heading = this.getAttribute("heading") ?? "Instant Quote";
    if (!cats.length) {
      this.root.innerHTML = `<style>${styles}</style><div class="shell"><p style="padding:1rem">No quote categories configured.</p></div>`;
      return;
    }
    const c = this.compute();
    const body = this.state.sent ? this.sentView() : `
      <div class="layout">
        <div class="form">${c.cat.type === "garment-formula" ? garmentForm(this.state, c.cat, this.mode, c.quote, this.uid) : tieredForm(this.state.t[c.cat.id], c.cat, c.cat.id)}
          ${this.contactView()}
        </div>
        <aside class="ticket" aria-live="polite"></aside>
      </div>
      <div class="mini-total"></div>`;

    this.root.innerHTML = `<style>${styles}</style>
      <div class="shell">
        <header class="top"><div><div class="eyebrow">National Media Services</div><h2 class="title">${esc(heading)}</h2></div>
          ${this.mode === "internal" ? `<span class="staff-badge">Staff mode</span>` : ""}</header>
        ${cats.length > 1 ? `<nav class="tabs" role="tablist">${cats.map((cat) => `<button role="tab" data-action="tab" data-kind="${esc(cat.id)}" aria-selected="${cat.id === c.cat.id}">${esc(cat.label)}</button>`).join("")}</nav>` : ""}
        ${body}
      </div>
      <dialog id="waiver-dlg"><div class="dlg-head">⚠️ Action required: print customer liability form</div>
        <div class="dlg-body"><p style="margin:0">This order contains customer-supplied garments. You must print and have the customer sign the physical liability waiver form before submitting this order to production.</p>
        <div class="actions"><button class="btn primary" data-action="print-waiver">Print waiver</button><button class="btn" data-action="close-dialog">Close</button></div></div></dialog>`;

    if (!this.state.sent) this.renderTicket(c);
    // Hide the pinned mini total while the full ticket is on screen.
    this.ticketObserver?.disconnect();
    const ticket = this.root.querySelector("aside.ticket"), mini = this.root.querySelector(".mini-total");
    if (ticket && mini && "IntersectionObserver" in window) {
      this.ticketObserver = new IntersectionObserver(([e]) => mini.classList.toggle("off", e.isIntersecting), { threshold: 0.25 });
      this.ticketObserver.observe(ticket);
    }

    if (key) {
      const el = this.root.querySelector(key);
      if (el) {
        el.focus({ preventScroll: true });
        if (caret != null && "setSelectionRange" in el) { try { el.setSelectionRange(caret, caret); } catch { /* number inputs */ } }
      }
    }
  }

  renderTicket(c = this.compute()) {
    const el = this.root.querySelector("aside.ticket");
    if (!el) return;
    const { cat, quote } = c;
    const statusText = { priced: "Ready", incomplete: "Needs info", "quote-requested": "Custom quote" }[quote.status];
    const body = cat.type === "garment-formula" ? garmentTicket(quote, this.state, cat, this.mode) : tieredTicket(quote, this.state.t[cat.id], cat, cat.id);
    const canSubmit = this.canSubmit(c);
    // An empty order reads "—", not "$0.00", so it can't be mistaken for a price.
    const totalText = quote.total == null || (quote.status === "incomplete" && !quote.total) ? "—" : usd(quote.total);
    const totalLabel = quote.status === "quote-requested" && quote.total == null ? "Custom quote" : quote.totalExcludesShipping ? "Total + shipping" : "Estimated total";
    el.innerHTML = `
      <div class="ticket-head"><h3>Your quote</h3><span class="status ${quote.status}">${statusText}</span></div>
      <div class="ticket-body">
        ${body}
        ${quote.errors.length ? `<ul class="issues">${quote.errors.slice(0, 6).map((e) => `<li>${esc(e)}</li>`).join("")}</ul>` : ""}
        ${quote.warnings.map((w) => `<p class="warn">${esc(w)}</p>`).join("")}
      </div>
      <div class="total"><span class="label">${totalLabel}</span>
        <span class="amount ${this.lastTotal !== null && this.lastTotal !== totalText ? "bump" : ""}">${totalText}</span></div>
      <div class="ticket-foot">
        ${this.mode === "internal" && cat.type === "garment-formula"
          ? `<button class="btn primary" data-action="print-quote" ${quote.status === "incomplete" ? "disabled" : ""}>Print internal quote</button>`
          : `<button class="btn primary" data-action="submit" ${canSubmit.ok ? "" : 'aria-disabled="true"'}>${quote.status === "quote-requested" ? "Request custom quote" : "Request this quote"}</button>
             ${canSubmit.ok ? "" : `<p class="note">${esc(canSubmit.reason)}</p>`}`}
        <p class="note">Estimate before sales tax. Final price is confirmed by NMS after reviewing your files.</p>
      </div>`;
    const mini = this.root.querySelector(".mini-total");
    if (mini) mini.innerHTML = `<span><span class="label">${totalLabel}</span><br><span class="amount">${totalText}</span></span>
      <button data-action="show-ticket">See quote ↓</button>`;
    this.lastTotal = totalText;
    this.dispatchEvent(new CustomEvent("nms-quote:change", { detail: { category: cat.id, quote }, bubbles: true, composed: true }));
  }

  contactView() {
    const c = this.state.contact;
    return `<fieldset><legend><span class="num">✉</span>Your details</legend>
      <div class="row">
        <label class="field">Name<input type="text" autocomplete="name" data-path="contact.name" value="${esc(c.name)}"></label>
        <label class="field">Email<input type="email" autocomplete="email" data-path="contact.email" value="${esc(c.email)}"></label>
        <label class="field">Phone<input type="tel" autocomplete="tel" data-path="contact.phone" value="${esc(c.phone)}"></label>
      </div>
      <label class="field" style="margin-top:.6rem">Notes for our team<textarea rows="2" data-path="contact.notes">${esc(c.notes)}</textarea></label>
    </fieldset>`;
  }

  sentView() {
    const s = this.state.sent;
    return `<div class="sent"><div class="eyebrow">Quote request ${esc(s.ref)}</div><h3>Thanks, we've got it.</h3>
      <p style="max-width:44ch;margin:0">We'll review your request and reply to <b>${esc(s.email)}</b> within one business day. Your estimate was <b>${s.total == null ? "a custom quote" : usd(s.total)}</b>.</p>
      <button class="btn" data-action="new-quote">Start another quote</button></div>`;
  }

  canSubmit({ cat, quote }) {
    if (quote.status === "incomplete") return { ok: false, reason: "Finish the highlighted details to request this quote." };
    if (cat.type === "garment-formula" && !this.state.g.copyright) return { ok: false, reason: "Confirm the copyright statement to continue." };
    const { name, email } = this.state.contact;
    if (!name.trim() || !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email)) return { ok: false, reason: "Add your name and email so we can send your quote." };
    return { ok: true };
  }

  // ---------- events ----------

  readValue(el) {
    const t = el.dataset.type;
    if (t === "bool") return el.type === "checkbox" ? el.checked : el.value === "true";
    if (t === "int") return el.value === "" ? "" : Math.max(0, parseInt(el.value, 10) || 0);
    return el.value;
  }

  // Text-like fields never trigger a full re-render: replacing the DOM while
  // someone tabs or clicks away from a field would drop that focus or click.
  // They update state, the ticket, and a few attributes in place instead.
  onInput(e) {
    const el = e.target;
    if (!el.dataset?.path || el.type === "radio" || el.type === "checkbox" || el.tagName === "SELECT") return;
    setPath(this.state, el.dataset.path, this.readValue(el));
    const c = this.compute();
    this.renderTicket(c);
    this.patchDom(c);
  }

  /** In-place updates for state that text fields can change. */
  patchDom({ cat, quote }) {
    if (cat.type !== "garment-formula") {
      const qty = Number(this.state.t[cat.id].quantity);
      this.root.querySelectorAll(".qty-picks button").forEach((b) => b.setAttribute("aria-pressed", String(Number(b.dataset.qty) === qty)));
      return;
    }
    const zip = String(this.state.g.delivery.zip ?? "").trim().slice(0, 5);
    const local = cat.delivery.courierZips.includes(zip);
    this.root.querySelectorAll("[data-requires-local]").forEach((n) => { n.hidden = !local && !n.querySelector("input:checked"); });
    quote.lines.forEach((l, li) => {
      (l.rowErrors ?? []).forEach((errs, ri) => {
        const prefix = `g.lines.${li}.roster.${ri}.`;
        const tr = this.root.querySelector(`[data-path^="${prefix}"]`)?.closest("tr");
        if (!tr) return;
        tr.classList.toggle("bad", errs.length > 0);
        tr.title = errs.map((x) => x.message).join(" ");
        tr.querySelectorAll("[data-path]").forEach((f) => {
          const field = f.dataset.path.slice(prefix.length);
          if (errs.some((x) => x.field === field)) f.setAttribute("aria-invalid", "true");
          else f.removeAttribute("aria-invalid");
        });
      });
    });
    this.root.querySelectorAll('input[data-path$=".note"]').forEach((f) => f.setAttribute("aria-invalid", String(!f.value.trim())));
  }

  onChange(e) {
    const el = e.target;
    const d = el.dataset ?? {};
    if (d.upload) return this.handleArtwork(el.files?.[0], ...d.upload.split(":").map(Number));
    if (d.rosterUpload) return this.handleRosterCsv(el.files?.[0], Number(d.rosterUpload));
    if (d.addon) {
      const s = this.state.t[d.key];
      s.addons = el.checked ? [...s.addons, d.addon] : s.addons.filter((a) => a !== d.addon);
      return this.render();
    }
    if (!d.path) return;
    const path = d.path;
    const textLike = el.tagName === "TEXTAREA" || (el.tagName === "INPUT" && !["radio", "checkbox"].includes(el.type));
    setPath(this.state, path, this.readValue(el));
    if (textLike) {
      const dm = path.match(/^g\.lines\.(\d+)\.decals\.(\d+)\.(widthIn|heightIn)$/);
      const decal = dm && this.state.g.lines[Number(dm[1])].decals[Number(dm[2])];
      if (decal?.art?.file) this.handleArtwork(decal.art.file, Number(dm[1]), Number(dm[2]));
      return;
    }

    // Follow-on updates that keep state consistent.
    const m = path.match(/^g\.lines\.(\d+)\.(productId|decals\.(\d+)\.(placement|oversized))$/);
    if (m && m[2] === "productId") {
      const cat = this.category;
      const line = this.state.g.lines[Number(m[1])];
      line.color = cat.catalog.products.find((p) => p.id === line.productId)?.colors?.[0] ?? "";
    } else if (m && m[3] != null) {
      const decal = this.state.g.lines[Number(m[1])].decals[Number(m[3])];
      if (decal.art?.file) return this.handleArtwork(decal.art.file, Number(m[1]), Number(m[3]));
    }
    if (path.startsWith("t.")) normalizeTiered(this.state.t[path.split(".")[1]], this.category);
    this.render();
  }

  onClick(e) {
    const row = e.target.closest?.("tr[data-qty]");
    if (row) {
      this.state.t[row.dataset.key].quantity = Number(row.dataset.qty);
      return this.render();
    }
    const btn = e.target.closest?.("[data-action]");
    if (!btn || btn.disabled) return;
    const d = btn.dataset;
    const g = this.state.g;
    const line = d.line != null ? g?.lines[Number(d.line)] : null;
    switch (d.action) {
      case "tab": this.state.active = d.kind; break;
      case "add-line": g.lines.push(newGarmentLine(this.category)); break;
      case "remove-line": g.lines.splice(Number(d.line), 1); break;
      case "set-kind":
        line.kind = d.kind;
        if (d.kind === "roster" && !line.roster.length) line.roster.push({ customer: "", size: "", color: "", name: "", number: "", qty: 1 });
        break;
      case "add-decal": line.decals.push(newDecal(line.decals.length ? "back" : "front")); break;
      case "remove-decal": line.decals.splice(Number(d.decal), 1); break;
      case "add-roster-row": line.roster.push({ customer: "", size: "", color: line.roster.at(-1)?.color ?? "", name: "", number: "", qty: 1 }); break;
      case "remove-roster-row": line.roster.splice(Number(d.row), 1); break;
      case "dismiss-roster-prompt": line.rosterPromptDismissed = true; break;
      case "roster-template": this.download("nms-roster-template.csv", rosterTemplateCsv(), "text/csv"); return;
      case "set-qty": this.state.t[d.key].quantity = Number(d.qty); break;
      case "apply-impose": {
        const s = this.state.t[d.key];
        const hint = suggestUp(s, this.category);
        if (hint?.up) { s.selections.up = String(hint.up); normalizeTiered(s, this.category); }
        break;
      }
      case "submit": e.preventDefault(); return this.submit();
      case "print-quote": return this.printQuote();
      case "print-waiver": {
        const c = this.compute();
        printWaiver(c.quote, this.state.contact);
        return;
      }
      case "close-dialog": this.root.getElementById("waiver-dlg").close(); return;
      case "new-quote": this.initState(); break;
      case "show-ticket": this.root.querySelector("aside.ticket")?.scrollIntoView({ behavior: "smooth", block: "start" }); return;
      default: return;
    }
    this.render();
  }

  onDrag(e, over) {
    const zone = e.target.closest?.("[data-drop]");
    if (!zone) return;
    e.preventDefault();
    zone.classList.toggle("over", over);
  }

  onDrop(e) {
    const zone = e.target.closest?.("[data-drop]");
    if (!zone) return;
    e.preventDefault();
    const [li, di] = zone.dataset.drop.split(":").map(Number);
    this.handleArtwork(e.dataTransfer.files?.[0], li, di);
  }

  async handleArtwork(file, li, di) {
    if (!file) return;
    const cat = this.category;
    const decal = this.state.g.lines[li]?.decals[di];
    if (!decal) return;
    const placement = cat.placements.find((p) => p.id === decal.placement);
    const result = await validateArtwork(file, { placement, oversized: decal.oversized, widthIn: decal.widthIn, heightIn: decal.heightIn }, cat.artwork, browserHasTransparentPixels).catch((err) => ({ ok: false, error: `Could not read this file (${err.message}).` }));
    if (decal.art?.url) URL.revokeObjectURL(decal.art.url);
    decal.art = {
      file,
      name: file.name,
      url: result.info ? URL.createObjectURL(file) : null,
      ok: result.ok,
      error: result.error ?? null,
      widthIn: result.info?.widthIn ?? 0,
      heightIn: result.info?.heightIn ?? 0,
      dpi: result.info?.dpiX ?? 0,
    };
    this.render();
  }

  async handleRosterCsv(file, li) {
    if (!file) return;
    const line = this.state.g.lines[li];
    const parsed = parseRosterCsv(await file.text());
    if (parsed.error) {
      alert(parsed.error);
      return;
    }
    const sizes = this.category.sizes;
    line.roster = parsed.rows.map((r) => ({ ...r, size: resolveSize(r.size, sizes) ?? r.size }));
    if (!line.roster.length) line.roster.push({ customer: "", size: "", color: "", name: "", number: "", qty: 1 });
    line.rosterPromptDismissed = true;
    this.render();
  }

  download(name, text, type) {
    const url = URL.createObjectURL(new Blob([text], { type }));
    const a = Object.assign(document.createElement("a"), { href: url, download: name });
    document.body.append(a);
    a.click();
    a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  }

  printQuote() {
    const c = this.compute();
    printGarmentQuote(c.quote, this.state.contact);
    if (c.order.lines.some((l) => l.customerSupplied)) this.root.getElementById("waiver-dlg").showModal();
  }

  /** Strip File objects and blob URLs so the payload is plain JSON. */
  serializableOrder(order) {
    return JSON.parse(JSON.stringify(order, (k, v) => (k === "file" || k === "url" ? undefined : v)));
  }

  async submit() {
    const c = this.compute();
    const check = this.canSubmit(c);
    if (!check.ok) {
      this.root.querySelector(".issues, [aria-invalid='true']")?.scrollIntoView({ behavior: "smooth", block: "center" });
      return;
    }
    const ref = `Q-${Date.now().toString(36).toUpperCase()}`;
    const detail = {
      ref,
      category: c.cat.id,
      order: this.serializableOrder(c.order),
      quote: c.quote,
      contact: { ...this.state.contact },
      artwork: c.cat.type === "garment-formula" ? this.state.g.lines.flatMap((l, li) => l.decals.map((d, di) => d.art?.ok && d.art.file && { line: li, decal: di, file: d.art.file }).filter(Boolean)) : [],
      createdAt: new Date().toISOString(),
    };
    const url = this.getAttribute("submit-url");
    if (url) {
      try {
        const form = new FormData();
        form.append("quote", JSON.stringify({ ...detail, artwork: detail.artwork.map(({ line, decal, file }) => ({ line, decal, name: file.name })) }));
        detail.artwork.forEach(({ line, decal, file }) => form.append(`artwork_${line}_${decal}`, file, file.name));
        const res = await fetch(url, { method: "POST", body: form });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
      } catch (err) {
        alert(`Sorry, we couldn't send your request (${err.message}). Please call (540) 635-4181.`);
        return;
      }
    }
    const proceed = this.dispatchEvent(new CustomEvent("nms-quote:submit", { detail, bubbles: true, composed: true, cancelable: true }));
    if (proceed) {
      this.state.sent = { ref, email: this.state.contact.email, total: c.quote.total };
      this.render();
    }
  }
}

if (!customElements.get("nms-quote")) customElements.define("nms-quote", NmsQuote);

export { NmsQuote };
export * as engine from "../engine/index.js";
